import type { Article } from '../types'

export const article: Article = {
  meta: {
    slug: 'romanticky-pobyt-ve-dvou',
    title: 'Romantika ve dvou: pobyt, kde jste konečně jen vy dva',
    metaTitle: 'Romantický pobyt ve dvou na chalupě',
    perex:
      'Telefon, který nepřestane blikat, a dva lidé, co se přes týden jen míjejí ve dveřích. A pak chalupa kdesi v lese, kde nikdo nezvoní a nikam se nespěchá – jen vy dva. Ukážeme, proč si pobyt jen pro dva dopřát a jak ho naplánovat.',
    coverImage: 'https://images.unsplash.com/photo-1601918774946-25832a4be0d6?w=1200&q=80&auto=format&fit=crop',
    coverImageAlt: 'Útulná dřevěná chata mezi stromy s rozsvíceným teplým světlem za soumraku',
    publishedAt: '2026-06-26',
    author: 'Redakce Mátevolno',
    tags: ['romantika', 'pro páry', 've dvou'],
  },
  content: `Telefon, který nepřestane blikat. Dva lidé, co se přes týden míjejí ve dveřích a pořádně si popovídají až u večerních zpráv. Zní to povědomě? Pak je nejvyšší čas někam zmizet – na místo, kde nebude nikdo a nic kromě vás dvou.

Žádná recepce, žádní sousedé za stěnou, žádný program, který musíte stihnout. Jen chalupa kdesi v lese, klika, kterou za sebou zavřete, a pár dní, které patří jenom vám.

> Nejhezčí na pobytu ve dvou není to, co podniknete. Je to, že to konečně podniknete spolu a beze spěchu.

## Soukromí, které hotel nenabídne

V hotelu jste pořád tak trochu mezi lidmi. Tenká stěna, chodba plná kufrů, snídaně do půl desáté a bazén, kde se mačká půl patra. Romantika se mezi to vejde jen těžko.

Chalupa jen pro vás dva tohle obrací. Celý dům je váš – kuchyň, terasa, zahrada i ticho kolem. Nikdo neklepe, nikdo nekouká. Můžete snídat o jedenácté, mlčet u kávy nebo se smát nahlas, aniž byste někoho rušili. Právě v tom soukromí se dvěma lidem nejlíp vrací to, na co přes týden není čas. Tady je výběr míst, kam můžete vyrazit jen ve dvou:

::nabidky{adult="true" beds="2" text="Prohlédnout pobyty jen pro dva"}

## Den, který nikam nespěchá

Kouzlo pobytu ve dvou je v tom, že si den poskládáte úplně po svém – a nikdo vám do něj nemluví.

### Ráno

Žádný budík, žádný bufet, který za chvíli zavře. Káva do postele, otevřené okno a dopoledne, které začne, až sami budete chtít.

### Přes den

Výlet do okolí, líné válení na zahradě, nebo obojí napůl. Když máte celý dům jen pro sebe, nemusíte se na ničem domlouvat s nikým dalším než spolu.

### Večer

Sklenka na terase, oheň nebo krb a ticho, které není trapné. Tady nikdo nehlásí poslední objednávky – večer končí, až když se vám bude chtít spát.

## Chalupa ve dvou, nebo romantika v hotelu?

Rozdíl není v tom, jestli budete spolu. Je v tom, jak moc vás při tom nechá svět na pokoji.

| Chalupa jen pro vás dva | Romantika v hotelu |
| --- | --- |
| Celý dům patří jen vám | Pokoj mezi desítkami dalších |
| Snídaně, kdy se vám zachce | Bufet jen do půl desáté |
| Ticho a soukromí na zahradě | Cizí lidé za stěnou |
| Večer u vlastního krbu nebo na terase | Společná hala a recepce |

## Jak naplánovat pobyt ve dvou

Pár drobností, díky kterým si čas o samotě užijete naplno:

- **Nechte doma seznam úkolů.** Pobyt ve dvou není na to, abyste „konečně něco stihli". Je na to, abyste nemuseli nic.
- **Vyberte místo podle nálady, ne podle programu.** Někdy je víc romantiky v zapadlé chalupě bez wi-fi než v atrakcích kolem.
- **Domluvte se na jedné společné věci.** Jeden výlet, jedna večeře, kterou uvaříte spolu – zbytek nechte plynout.
- **Vezměte si, co máte oba rádi.** Oblíbené víno, něco dobrého na zub, desku nebo knížku. Maličkosti dělají atmosféru.
- **Naplánujte si i čas, kdy neděláte vůbec nic.** Právě ten si nejspíš zapamatujete nejvíc.

## Zmizte spolu

Když na sebe přes týden sotva najdete čas, pár dní jen ve dvou udělá víc než dlouhá dovolená v davu. Vyberte si chalupu, zavřete za sebou dveře a nechte svět na chvíli za nimi:

::cta{text="Prohlédnout všechny pobyty"}`,
}
